import React, { useCallback, Component } from "react";
import { useWorldState } from "../state/WorldStateContext.jsx";
import LegalTeamSelection, { hireLegalTeam, createLegalState } from "./LegalTeamSelection.jsx";
import SlotNegotiation, { generateAvailableSlots, generateCompetitors } from "./SlotNegotiation.jsx";
import NegotiationResult, {
  generateSealedCompetitorBids,
  determineWinners,
  generateReputationChanges,
  generateRelationshipChanges,
} from "./NegotiationResult.jsx";
import { debug } from '../../lib/debug.js';

// ============================================================================
// ERROR BOUNDARY FOR DEBUGGING
// ============================================================================
class NegotiationErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[NegotiationErrorBoundary] Caught error:", error);
    console.error("[NegotiationErrorBoundary] Error info:", errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-red-900 border border-red-500 rounded-lg p-6 max-w-lg">
            <h2 className="text-xl font-bold text-red-300 mb-4">Negotiation Error</h2>
            <p className="text-red-200 mb-2">Something went wrong in the negotiation flow:</p>
            <pre className="bg-black/50 p-3 rounded text-xs text-red-300 overflow-auto max-h-60">
              {this.state.error?.toString()}
              {this.state.errorInfo?.componentStack}
            </pre>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============================================================================
// NEGOTIATION CONTROLLER
// ============================================================================
// Manages the flow: Legal Selection -> Slot Bidding -> Result Reveal
// ============================================================================

export default function NegotiationController() {
  const {
    worldState,
    updateNegotiation,
    completeNegotiation,
    cancelNegotiation,
    updateLegalState,
    setWorldState,
  } = useWorldState();

  const negotiation = worldState?.negotiation;

  // ============================================================================
  // ALL HOOKS MUST BE BEFORE ANY CONDITIONAL RETURNS
  // ============================================================================

  const handleLegalSelect = useCallback((team) => {
    debug("[NegotiationController] handleLegalSelect called", { team, worldState });
    if (!worldState) {
      console.error("[NegotiationController] worldState is null/undefined");
      return;
    }
    try {
      const currentLegal = worldState.legal || createLegalState();
      debug("[NegotiationController] currentLegal:", currentLegal);
      const newLegalState = hireLegalTeam(currentLegal, team, worldState.gameTime);
      debug("[NegotiationController] newLegalState:", newLegalState);
      updateLegalState(newLegalState);
      debug("[NegotiationController] Legal state updated, now updating negotiation phase to 'bidding'");
      updateNegotiation({ phase: 'bidding' });
      debug("[NegotiationController] Negotiation phase updated");
    } catch (error) {
      console.error("[NegotiationController] Error in handleLegalSelect:", error);
    }
  }, [worldState, updateLegalState, updateNegotiation]);

  const handleLegalSkip = useCallback(() => {
    updateNegotiation({ phase: 'bidding' });
  }, [updateNegotiation]);

  const handleBidSubmit = useCallback((bidData) => {
    if (!negotiation) return;
    const { slots, bidPerSlot, totalCost, legalFirm, feeReduction } = bidData;

    const playerBids = slots.map(slotId => ({
      slotId,
      amount: bidPerSlot,
    }));

    // Use pre-generated sealed bids (AI bids were determined BEFORE player submitted)
    // This is a true sealed-bid auction - AI doesn't see player's bid
    const competitorBids = negotiation.sealedCompetitorBids || [];

    // Per Protocol 8.2: Tie-breaker uses relations with airport
    const results = determineWinners(
      playerBids,
      competitorBids,
      negotiation.airportCode,
      worldState?.relations
    );
    const reputationChanges = generateReputationChanges(results, negotiation.airportCode);
    const relationshipChanges = generateRelationshipChanges(results, competitorBids);

    const slotsWon = results.filter(r => r.winner === 'player').length;
    const facilityFeePerSlot = 50000;
    const firstMonthLeasePerSlot = Math.round(30000 * (1 - (feeReduction || 0)));
    const actualTotalCost = slotsWon * (facilityFeePerSlot + bidPerSlot + firstMonthLeasePerSlot);

    updateNegotiation({
      phase: 'result',
      playerBids,
      competitorBids,
      results,
      reputationChanges,
      relationshipChanges,
      totalCost: actualTotalCost,
    });
  }, [negotiation, updateNegotiation]);

  const handleBidDecline = useCallback(() => {
    if (!negotiation) return;
    const historyEntry = {
      ...negotiation,
      outcome: 'declined',
      completedAt: new Date().toISOString(),
    };

    setWorldState(prev => ({
      ...prev,
      negotiation: null,
      negotiationHistory: [...(prev.negotiationHistory || []), historyEntry],
      isPaused: false,
    }));
  }, [negotiation, setWorldState]);

  const handleGoBack = useCallback(() => {
    updateNegotiation({ phase: 'legal' });
  }, [updateNegotiation]);

  const handleResultContinue = useCallback(() => {
    if (!negotiation) return;
    completeNegotiation({
      airportCode: negotiation.airportCode,
      playerBids: negotiation.playerBids,
      results: negotiation.results,
      totalCost: negotiation.totalCost,
      reputationChanges: negotiation.reputationChanges,
      relationshipChanges: negotiation.relationshipChanges,
    });
  }, [negotiation, completeNegotiation]);

  const handleViewSchedule = useCallback(() => {
    handleResultContinue();
  }, [handleResultContinue]);

  // ============================================================================
  // EARLY RETURN - After all hooks
  // ============================================================================

  if (!negotiation) {
    return null;
  }

  // ============================================================================
  // RENDER BASED ON PHASE
  // ============================================================================

  if (negotiation.phase === 'legal') {
    return (
      <LegalTeamSelection
        airportCode={negotiation.airportCode}
        airportName={negotiation.airportName}
        onSelect={handleLegalSelect}
        onSkip={handleLegalSkip}
        worldState={worldState}
      />
    );
  }

  if (negotiation.phase === 'bidding') {
    debug("[NegotiationController] Rendering SlotNegotiation phase", {
      airportCode: negotiation.airportCode,
      availableSlots: negotiation.availableSlots,
      hasWorldState: !!worldState,
    });
    return (
      <NegotiationErrorBoundary>
        <SlotNegotiation
          airportCode={negotiation.airportCode}
          airportName={negotiation.airportName}
          availableSlots={negotiation.availableSlots}
          competitors={negotiation.competitors}
          playerSchedule={worldState.flights?.filter(f => f.phase !== 'completed' && f.phase !== 'cancelled') || []}
          hubCode={worldState.airline?.hub || "KPDX"}
          worldState={worldState}
          onBidSubmit={handleBidSubmit}
          onDecline={handleBidDecline}
          onGoBack={handleGoBack}
        />
      </NegotiationErrorBoundary>
    );
  }

  if (negotiation.phase === 'result') {
    return (
      <NegotiationResult
        airportCode={negotiation.airportCode}
        airportName={negotiation.airportName}
        playerBids={negotiation.playerBids}
        competitorBids={negotiation.competitorBids}
        results={negotiation.results}
        reputationChanges={negotiation.reputationChanges}
        relationshipChanges={negotiation.relationshipChanges}
        totalCost={negotiation.totalCost}
        onContinue={handleResultContinue}
        onViewSchedule={handleViewSchedule}
      />
    );
  }

  return null;
}

// ============================================================================
// AIRPORT UNLOCK TIERS
// ============================================================================
// Airports are grouped by tier - higher RepScore unlocks more markets
// RepScore thresholds: Tier 1 (0), Tier 2 (1000), Tier 3 (3000), Tier 4 (7000), Tier 5 (15000)
// ============================================================================

export const AIRPORT_TIERS = {
  // Tier 1: Pacific Northwest regional (available from start - RepScore 0+)
  1: {
    minRepScore: 0,
    airports: ["KSEA", "KBOI", "KRDM", "KSMF", "KPDX"],
    description: "Pacific Northwest Regional",
  },
  // Tier 2: West Coast expansion (RepScore 1000+)
  2: {
    minRepScore: 1000,
    airports: ["KLAS", "KPHX", "KSLC", "KDEN", "KABQ"],
    description: "West Coast Expansion",
  },
  // Tier 3: California majors (RepScore 3000+)
  3: {
    minRepScore: 3000,
    airports: ["KLAX", "KSFO", "KSAN", "KOAK", "KSJC"],
    description: "California Majors",
  },
  // Tier 4: National hub markets (RepScore 7000+)
  4: {
    minRepScore: 7000,
    airports: ["KORD", "KDFW", "KATL", "KMIA", "KJFK"],
    description: "National Hub Markets",
  },
  // Tier 5: International gateways (RepScore 15000+)
  5: {
    minRepScore: 15000,
    airports: ["EGLL", "LFPG", "RJTT", "VHHH", "WSSS"],
    description: "International Gateways",
  },
};

// ============================================================================
// HELPER: Check if airport is unlocked for negotiation
// ============================================================================

export function isAirportUnlocked(worldState, airportCode) {
  // Check explicit unlocks (HRD purchase, invitation, etc.)
  if (worldState.unlockedAirports?.[airportCode]) {
    return true;
  }

  // Check tier-based unlocks using RepScore (0-15000+ scale)
  const repScore = worldState.reputation?.currentScore || 0;
  for (const [tier, tierData] of Object.entries(AIRPORT_TIERS)) {
    if (repScore >= tierData.minRepScore && tierData.airports.includes(airportCode)) {
      return true;
    }
  }

  return false;
}

// ============================================================================
// HELPER: Check if player can initiate negotiation at airport
// ============================================================================

export function canNegotiateAt(worldState, airportCode) {
  // Already in negotiation?
  if (worldState.negotiation) {
    return { canNegotiate: false, reason: "Already in a negotiation" };
  }

  // Is it the hub? (Hub slots are free, no negotiation needed)
  if (airportCode === worldState.airline?.hub) {
    return { canNegotiate: false, reason: "This is your hub - slots included" };
  }

  // Already have slots at this airport?
  const existingSlots = worldState.slots?.[airportCode];
  if (existingSlots && existingSlots.length > 0) {
    return { canNegotiate: false, reason: `You already have ${existingSlots.length} slot(s) here` };
  }

  // Is the airport unlocked?
  if (!isAirportUnlocked(worldState, airportCode)) {
    // Find what tier this airport is in
    for (const [tier, tierData] of Object.entries(AIRPORT_TIERS)) {
      if (tierData.airports.includes(airportCode)) {
        const currentRepScore = worldState.reputation?.currentScore || 0;
        return {
          canNegotiate: false,
          reason: `Requires ${tierData.minRepScore.toLocaleString()} RepScore (Tier ${tier}: ${tierData.description}). You have ${currentRepScore.toLocaleString()}.`,
          unlockMethod: "reputation",
          requiredRepScore: tierData.minRepScore,
          tier: parseInt(tier),
        };
      }
    }
    return {
      canNegotiate: false,
      reason: "Airport not available - purchase HRD intel to unlock",
      unlockMethod: "hrd",
    };
  }

  // All checks passed
  return { canNegotiate: true, reason: "Available for negotiation" };
}

// ============================================================================
// HELPER: Get all airports available for negotiation
// ============================================================================

export function getAvailableAirports(worldState) {
  const available = [];
  const repScore = worldState.reputation?.currentScore || 0;

  for (const [tier, tierData] of Object.entries(AIRPORT_TIERS)) {
    if (repScore >= tierData.minRepScore) {
      for (const code of tierData.airports) {
        const status = canNegotiateAt(worldState, code);
        if (status.canNegotiate) {
          available.push({
            code,
            tier: parseInt(tier),
            tierDescription: tierData.description,
          });
        }
      }
    }
  }

  // Also check explicitly unlocked airports (HRD, invitations, etc.)
  for (const [code, unlock] of Object.entries(worldState.unlockedAirports || {})) {
    const status = canNegotiateAt(worldState, code);
    if (status.canNegotiate && !available.find(a => a.code === code)) {
      available.push({
        code,
        tier: 0,
        tierDescription: unlock.unlockReason,
      });
    }
  }

  return available;
}

// ============================================================================
// HELPER: Get airport tier (1-5)
// ============================================================================

export function getAirportTier(airportCode) {
  for (const [tier, tierData] of Object.entries(AIRPORT_TIERS)) {
    if (tierData.airports.includes(airportCode)) {
      return parseInt(tier);
    }
  }
  // Default to tier 1 for unknown airports
  return 1;
}

// ============================================================================
// HELPER: Create negotiation data for an airport
// Per CLAUDE_PROTOCOL.md:
// - Sealed bids are generated at negotiation creation - AI doesn't see player bids
// - AI competitors consider their relations with the airport
// - Hostile competitors bid more aggressively against player
// ============================================================================

export function createNegotiationData(airportCode, airportName, hubCode, flightTimeMinutes = 80, relationsState = null) {
  const availableSlots = generateAvailableSlots(airportCode, hubCode, flightTimeMinutes, 3);
  // Generate competitors based on who has presence at this airport
  const competitors = generateCompetitors(3, airportCode, relationsState);
  const airportTier = getAirportTier(airportCode);

  // Major hub airports (Tier 4-5) have higher competition
  const isHub = airportTier >= 4;

  // Generate sealed competitor bids NOW, before player sees anything
  // AI bids consider: tier, relations with airport, hostility toward player
  const sealedCompetitorBids = generateSealedCompetitorBids(
    availableSlots,
    competitors,
    airportTier,
    { isHub, totalSlots: 10, airportCode },
    relationsState
  );

  return {
    airportCode,
    airportName,
    availableSlots,
    competitors,
    airportTier,
    sealedCompetitorBids, // AI bids locked in at negotiation start
  };
}
